import {
  BreakpointsEnum,
  isBreakpointUp,
  useBreakpoint,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {Flex, Pressable, Text, View} from 'native-base';
import React, {useState} from 'react';
import Icon from 'react-native-vector-icons/FontAwesome5Pro';

import PaymentCard from 'components/PaymentCard';
import {CreditCardType} from 'components/PaymentCard/PaymentCard';
import {useTheme} from 'styled-components/native';

import WarningModal from 'components/WarningModal';
import {PaymentCardInfo} from 'store/payments/payments.reducer';
import {CardIssuerNameLookup} from 'services/api/paymentintegration';

interface PaymentMethodProps {
  savedCard: PaymentCardInfo;
  onDeletePayment: (card: PaymentCardInfo) => void;
  index?: number;
}

function toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export const PaymentMethod = (props: PaymentMethodProps) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const breakpoint = useBreakpoint();

  const isMediumUp = isBreakpointUp(breakpoint, BreakpointsEnum.MD);
  const {caesarRewards} = theme.colors as any;

  const {savedCard, index} = props;
  const {cardMask, cardNickname, cardExpiration, cardExpired} = savedCard;

  const cardIssuer = CardIssuerNameLookup(savedCard.cardIssuer);

  const lastFour = cardMask.slice(-4);

  const deleteIcon = (
    <Icon
      size={14}
      name="trash-alt"
      style={{
        width: 14,
        height: 14,
        textAlign: 'center',
        color: caesarRewards,
      }}
    />
  );

  const [showModal, setShowModal] = useState(false);

  const handleDeletePressed = () => {
    setShowModal(true);
  };

  const handleConfirmDeletePressed = () => {
    props.onDeletePayment(savedCard);
    setShowModal(false);
  };

  return (
    <Flex
      borderColor="rgba(112,119,132,0.3)"
      borderRadius="8px"
      borderWidth="1px">
      <Flex
        flex={1}
        p={isDesktop ? '48px' : '24px'}
        direction="row"
        alignItems="start">
        <View flex={1} flexDirection={isMediumUp ? 'row' : 'column-reverse'}>
          <PaymentCard
            cardNickname={cardNickname}
            cardNumber={lastFour}
            cardType={(cardIssuer as CreditCardType) ?? CreditCardType.generic}
          />
          <View
            mb={isDesktop ? null : '24px'}
            flexShrink={1}
            justifyContent={'center'}
            ml={isMediumUp ? '48px' : null}>
            <Text
              mb={isDesktop ? '6px' : '5px'}
              fontFamily="heading"
              fontWeight="bold"
              numberOfLines={1}
              paddingRight="4px"
              fontSize={isDesktop ? '32px' : '18px'}
              lineHeight={isDesktop ? '40px' : '20px'}>
              {cardNickname || `My Saved Card ${index || ''}`}
            </Text>
            <Text
              fontSize={isDesktop ? '16px' : '14px'}
              color="paleSky"
              lineHeight={isDesktop ? '22px' : '16px'}>
              {`${
                cardIssuer === 'None' ? 'Card' : toTitleCase(cardIssuer)
              } ending in ${lastFour}`}
            </Text>
            {cardExpired ? (
              <Text
                fontSize={isDesktop ? '16px' : '14px'}
                color="error"
                fontWeight={700}
                lineHeight={isDesktop ? '22px' : '16px'}>
                Card is expired - {`Expiration: ${cardExpiration}`}
              </Text>
            ) : (
              <Text
                fontSize={isDesktop ? '16px' : '14px'}
                color="paleSky"
                lineHeight={isDesktop ? '22px' : '16px'}>
                {`Expiration: ${cardExpiration}`}
              </Text>
            )}
          </View>
        </View>
        <View alignSelf="flex-start" flexDir="row">
          <Pressable
            flexDir="row"
            alignItems="center"
            onPress={handleDeletePressed}>
            {deleteIcon}
            {isDesktop && (
              <Text ml="4px" fontWeight="semibold" color="caesarRewards">
                Delete
              </Text>
            )}
          </Pressable>
        </View>
      </Flex>
      <WarningModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        primaryCTATitle="Delete"
        body="By deleting, you will no longer have access to use this payment information as a payment method."
        title="Are you sure?"
        secondaryCTATitle="Cancel"
        onPrimaryPress={handleConfirmDeletePressed}
        onSecondaryPress={() => setShowModal(false)}
        isSecondaryRightAligned={true}
        equallySpacedButtons
      />
    </Flex>
  );
};

export default PaymentMethod;
